import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

const PageContainer = styled.div`
  ${tw`w-full max-w-full min-w-0 box-border container mx-auto px-4 py-8 max-w-4xl`}
`;

const Title = styled.h1`
  ${tw`text-3xl font-bold mb-6 text-text-primary`}
`;

const Section = styled.section`
  ${tw`mb-8`}
`;

const SectionTitle = styled.h2`
  ${tw`text-2xl font-semibold mb-4 text-text-primary`}
`;

const Paragraph = styled.p`
  ${tw`text-text-secondary mb-4 leading-relaxed`}
`;

const List = styled.ul`
  ${tw`list-disc list-inside mb-4 space-y-2 text-text-secondary`}
`;

const ListItem = styled.li`
  ${tw`mb-2`}
`;

const HighlightBox = styled.div`
  ${tw`bg-bg-secondary border-l-4 border-black p-4 mb-6 rounded-md`}
`;

const HighlightText = styled.p`
  ${tw`text-blue-900 font-medium`}
`;

const CodeBlock = styled.div`
  ${tw`bg-bg-secondary p-4 rounded-md mb-4 font-mono text-sm`}
`;

const ImageContainer = styled.div`
  ${tw`mb-6`}
`;

const ImageWrapper = styled.div`
  ${tw`mb-4 border border-border-default rounded-lg overflow-hidden shadow-card`}
`;

const ScreenshotImage = styled.img`
  ${tw`w-full h-auto`}
  max-width: 100%;
  display: block;
`;

const MobileScreenshotImage = styled.img`
  ${tw`h-auto mx-auto`}
  max-width: 300px;
  width: 100%;
  display: block;
`;

const ImageCaption = styled.p`
  ${tw`text-sm text-text-secondary mt-2 px-4 pb-4`}
`;

const ImageGrid = styled.div`
  ${tw`grid grid-cols-1 md:grid-cols-2 gap-4 mb-6`}
`;

const PlatformSection = styled.div`
  ${tw`mb-8 w-full max-w-full min-w-0 box-border`}
`;

const OptInWorkflowPage: React.FC = () => {
  return (
    <PageContainer>
      <Title>SMS Verification Opt-In Process</Title>

      <Section>
        <Paragraph>
          At Autopilotee, we require users to explicitly opt-in to receive SMS verification codes.
          This document describes our compliant opt-in workflow for phone number verification.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>1. User Initiation</SectionTitle>
        <Paragraph>
          Users can opt-in to receive SMS verification codes (OTP) through multiple methods:
        </Paragraph>
        <List>
          <ListItem>
            <strong>During booking/reservation:</strong> Users are prompted to verify their phone
            number when creating a booking or reservation. This requires explicit consent before any
            SMS is sent.
          </ListItem>
          <ListItem>
            <strong>From Account Settings:</strong> Users can enable or disable SMS text message
            notifications directly from their Account settings page on both Web and Mobile
            platforms. This provides users with ongoing control over their SMS preferences.
          </ListItem>
          <ListItem>
            <strong>When updating profile:</strong> Users can initiate phone verification from their
            profile page when updating account information.
          </ListItem>
        </List>
        <Paragraph tw="mt-4">
          <strong>Platform Support:</strong> This opt-in workflow is implemented on both our
          <strong> Web platform</strong> (accessible via web browsers) and our
          <strong> Mobile application</strong> (iOS and Android native apps), ensuring consistent
          consent collection across all user touchpoints.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>1.1. Platform-Specific Implementation</SectionTitle>

        <Paragraph>
          <strong>Web Platform:</strong>
        </Paragraph>
        <List>
          <ListItem>Users access the verification modal through a web browser interface</ListItem>
          <ListItem>
            The consent message is displayed in a modal dialog with a highlighted consent box
          </ListItem>
          <ListItem>
            Users interact with standard web form elements (input fields and buttons)
          </ListItem>
          <ListItem>
            The verification flow is accessible on desktop and mobile web browsers
          </ListItem>
        </List>

        <Paragraph tw="mt-4">
          <strong>Mobile Application (iOS & Android):</strong>
        </Paragraph>
        <List>
          <ListItem>
            Users access the verification modal through the native mobile app interface
          </ListItem>
          <ListItem>
            The consent message is displayed in a native modal component with a styled consent box
          </ListItem>
          <ListItem>
            Users interact with native mobile UI components optimized for touch input
          </ListItem>
          <ListItem>
            The phone number input uses the phone-pad keyboard for better user experience
          </ListItem>
          <ListItem>
            The verification code input uses the number-pad keyboard for quick entry
          </ListItem>
          <ListItem>
            The same explicit consent workflow applies: users must manually enter their phone
            number, read the consent message, and tap the "Send Code" button to provide consent
          </ListItem>
          <ListItem>
            The consent box displays the same messaging as the web platform, ensuring consistency
            across all platforms
          </ListItem>
          <ListItem>
            <strong>Entry Points:</strong> Phone verification is triggered in two scenarios on
            mobile:
            <List>
              <ListItem>When creating a booking or reservation (checkout flow)</ListItem>
              <ListItem>When updating profile information</ListItem>
            </List>
          </ListItem>
        </List>

        <Paragraph tw="mt-4">
          Both platforms implement the identical opt-in workflow with platform-appropriate UI/UX,
          ensuring that users on any device receive the same clear consent messaging and must take
          the same explicit actions to opt-in to SMS verification.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>2. Explicit Consent Required</SectionTitle>
        <Paragraph>
          Before any SMS is sent, users must take the following actions on both Web and Mobile
          platforms:
        </Paragraph>
        <List>
          <ListItem>
            <strong>Enter their phone number</strong> manually in the verification modal/screen
          </ListItem>
          <ListItem>
            <strong>Read and acknowledge</strong> the consent message that clearly states:
          </ListItem>
        </List>

        <HighlightBox>
          <HighlightText>
            "By clicking 'Send Code', you consent to receive a one-time verification code via SMS to
            the phone number you entered. Message and data rates may apply. This is a transactional
            message for account verification purposes only."
          </HighlightText>
        </HighlightBox>

        <List>
          <ListItem>
            <strong>Click/Tap the "Send Code" button</strong> - this action explicitly indicates
            their consent to receive an SMS verification code. On web, users click the button; on
            mobile, users tap the button.
          </ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>3. Consent Method</SectionTitle>
        <Paragraph>Our opt-in method uses explicit affirmative action:</Paragraph>
        <List>
          <ListItem>
            <strong>User-initiated:</strong> The user must manually enter their phone number
          </ListItem>
          <ListItem>
            <strong>Button click consent:</strong> The user must click "Send Code" to indicate
            consent
          </ListItem>
          <ListItem>
            <strong>Clear messaging:</strong> The consent message is displayed prominently above the
            "Send Code" button
          </ListItem>
          <ListItem>
            <strong>No pre-checked boxes:</strong> Users cannot be opted-in without their explicit
            action
          </ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>4. Message Type - Transactional Only</SectionTitle>
        <Paragraph>All SMS messages sent through our system are:</Paragraph>
        <List>
          <ListItem>
            <strong>One-time verification codes (OTP):</strong> 6-digit numeric codes
          </ListItem>
          <ListItem>
            <strong>Single-use only:</strong> Each code can only be used once
          </ListItem>
          <ListItem>
            <strong>Time-limited:</strong> Codes expire after 5-10 minutes
          </ListItem>
          <ListItem>
            <strong>Transactional only:</strong> Used exclusively for account/phone verification
          </ListItem>
          <ListItem>
            <strong>No marketing content:</strong> We do not send promotional or marketing messages
            via SMS
          </ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>5. User Control and Opt-Out</SectionTitle>
        <Paragraph>
          Users have full control over SMS verification and can manage their preferences:
        </Paragraph>
        <List>
          <ListItem>
            <strong>Opt-In Control:</strong> Users can enable or disable SMS text message
            notifications from their Account settings page on both Web and Mobile platforms:
            <List>
              <ListItem>
                <strong>Web Platform:</strong> In the Account settings page, users can check/uncheck
                the "Enable text message notifications" checkbox to opt-in or opt-out of SMS
                messages.
              </ListItem>
              <ListItem>
                <strong>Mobile Platform:</strong> In the Notification settings screen, users can
                toggle the "Enable text message notifications" switch to opt-in or opt-out of SMS
                messages.
              </ListItem>
            </List>
          </ListItem>
          <ListItem>
            <strong>During Verification:</strong> Users can choose not to verify their phone number,
            close the verification modal at any time, or decline to receive SMS (though some
            features may require verification).
          </ListItem>
          <ListItem>
            <strong>Phone Number Changes:</strong> Users can change their phone number before
            clicking "Send Code" during the verification process.
          </ListItem>
          <ListItem>
            <strong>Opt-Out Methods:</strong> Users can opt-out at any time by:
            <List>
              <ListItem>Disabling SMS notifications in Account/Notification settings</ListItem>
              <ListItem>Replying "STOP" to any SMS message (if supported)</ListItem>
              <ListItem>Contacting support to request removal from SMS communications</ListItem>
            </List>
          </ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>6. Opt-In Workflow Screenshots</SectionTitle>
        <Paragraph>
          The following screenshots demonstrate our opt-in workflow implementation on both Web and
          Mobile platforms:
        </Paragraph>

        <PlatformSection>
          <SectionTitle tw="text-xl mb-3">Web Platform Screenshots</SectionTitle>

          <Paragraph tw="mb-4">
            <strong>Booking/Checkout Flow:</strong>
          </Paragraph>

          <ImageGrid>
            <ImageContainer>
              <ImageWrapper>
                <ScreenshotImage
                  src="/sms-opt-in-screenshots/web-phone-input-checkout.png"
                  alt="Web platform - Phone number input during checkout"
                  onError={e => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <ImageCaption>
                  <strong>Checkout Flow - Phone Input:</strong> During the booking checkout process,
                  users are prompted to verify their phone number. The verification modal displays a
                  phone number input field with a prominent consent message box. Users can see the
                  consent text clearly stating that clicking "Send Code" will send an SMS
                  verification code.
                </ImageCaption>
              </ImageWrapper>
            </ImageContainer>

            <ImageContainer>
              <ImageWrapper>
                <ScreenshotImage
                  src="/sms-opt-in-screenshots/web-code-verification-checkout.png"
                  alt="Web platform - Verification code input during checkout"
                  onError={e => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <ImageCaption>
                  <strong>Checkout Flow - Code Verification:</strong> After the user clicks "Send
                  Code" and provides explicit consent, they receive an SMS with a verification code.
                  The modal transitions to show a code input field where users can enter the 6-digit
                  OTP code they received via SMS.
                </ImageCaption>
              </ImageWrapper>
            </ImageContainer>
          </ImageGrid>

          <Paragraph tw="mb-4 mt-6">
            <strong>Profile Page Flow:</strong>
          </Paragraph>

          <ImageGrid>
            <ImageContainer>
              <ImageWrapper>
                <ScreenshotImage
                  src="/sms-opt-in-screenshots/web-phone-input-profile.png"
                  alt="Web platform - Phone number input from profile"
                  onError={e => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <ImageCaption>
                  <strong>Profile Flow - Phone Input:</strong> Users can also initiate phone
                  verification from their profile page when updating their account information. The
                  same consent message is displayed, ensuring consistent opt-in workflow across all
                  entry points.
                </ImageCaption>
              </ImageWrapper>
            </ImageContainer>

            <ImageContainer>
              <ImageWrapper>
                <ScreenshotImage
                  src="/sms-opt-in-screenshots/web-code-verification-profile.png"
                  alt="Web platform - Verification code input from profile"
                  onError={e => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <ImageCaption>
                  <strong>Profile Flow - Code Verification:</strong> The verification code entry
                  screen from the profile flow. Users can see their phone number, enter the received
                  OTP code, and complete verification.
                </ImageCaption>
              </ImageWrapper>
            </ImageContainer>
          </ImageGrid>

          <Paragraph tw="mb-4 mt-6">
            <strong>Account Settings - SMS Notification Control:</strong>
          </Paragraph>

          <ImageContainer>
            <ImageWrapper>
              <ScreenshotImage
                src="/sms-opt-in-screenshots/web-account-settings-sms-enabled.png"
                alt="Web platform - Account settings with SMS notifications"
                width={400}
                onError={e => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <ImageCaption>
                <strong>Web Platform - Account Settings (Opt-In/Opt-Out):</strong> On the web
                platform, users can opt-in or opt-out of SMS text message notifications by checking
                or unchecking the "Enable text message notifications" checkbox in their Account
                settings page. This provides users with ongoing control over their SMS preferences
                on the web platform, allowing them to manage their opt-in status at any time.
              </ImageCaption>
            </ImageWrapper>
          </ImageContainer>
        </PlatformSection>

        <PlatformSection>
          <SectionTitle tw="text-xl mb-3">
            Mobile Application Screenshots (iOS & Android)
          </SectionTitle>

          <Paragraph tw="mb-4">
            <strong>Note:</strong> The mobile application supports phone verification in two
            scenarios: during checkout/booking and from the profile page.
          </Paragraph>

          <ImageGrid>
            <ImageContainer>
              <ImageWrapper>
                <MobileScreenshotImage
                  src="/sms-opt-in-screenshots/mobile-phone-input-checkout.png"
                  alt="Mobile app - Phone input during checkout"
                  onError={e => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <ImageCaption>
                  <strong>Checkout Flow - Phone Input:</strong> During the booking checkout process,
                  users are prompted to verify their phone number. The modal displays the phone
                  input field with the consent message, ensuring users see the opt-in consent before
                  proceeding.
                </ImageCaption>
              </ImageWrapper>
            </ImageContainer>

            <ImageContainer>
              <ImageWrapper>
                <MobileScreenshotImage
                  src="/sms-opt-in-screenshots/mobile-phone-input-profile.png"
                  alt="Mobile app - Phone input from profile"
                  onError={e => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <ImageCaption>
                  <strong>Profile Flow - Phone Input:</strong> Users can also initiate phone
                  verification from their profile page. The same consent message is displayed,
                  ensuring consistent opt-in workflow across all entry points.
                </ImageCaption>
              </ImageWrapper>
            </ImageContainer>
          </ImageGrid>

          <ImageGrid>
            <ImageContainer>
              <ImageWrapper>
                <MobileScreenshotImage
                  src="/sms-opt-in-screenshots/mobile-code-verification-checkout.png"
                  alt="Mobile app - Code verification during checkout"
                  onError={e => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <ImageCaption>
                  <strong>Checkout Flow - Code Verification:</strong> After providing consent and
                  receiving the SMS code, users enter the verification code in the input field. The
                  interface shows the phone number being verified and provides options to change the
                  number or resend the code.
                </ImageCaption>
              </ImageWrapper>
            </ImageContainer>

            <ImageContainer>
              <ImageWrapper>
                <MobileScreenshotImage
                  src="/sms-opt-in-screenshots/mobile-code-verification-profile.png"
                  alt="Mobile app - Code verification from profile"
                  onError={e => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <ImageCaption>
                  <strong>Profile Flow - Code Verification:</strong> The verification code entry
                  screen from the profile flow. Users can see their phone number, enter the received
                  OTP code, and complete verification.
                </ImageCaption>
              </ImageWrapper>
            </ImageContainer>
          </ImageGrid>

          <Paragraph tw="mb-4 mt-6">
            <strong>Notification Settings - SMS Notification Control:</strong>
          </Paragraph>

          <ImageContainer>
            <ImageWrapper>
              <MobileScreenshotImage
                src="/sms-opt-in-screenshots/mobile-notification-settings-sms-enabled.png"
                alt="Mobile app - Notification settings with SMS notifications"
                onError={e => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <ImageCaption>
                <strong>Mobile Platform - Notification Settings (Opt-In/Opt-Out):</strong> On the
                mobile application (iOS & Android), users can opt-in or opt-out of SMS text message
                notifications by toggling the "Enable text message notifications" switch to ON or
                OFF in the Notification settings screen. This provides users with ongoing control
                over their SMS preferences on mobile devices, allowing them to manage their opt-in
                status at any time.
              </ImageCaption>
            </ImageWrapper>
          </ImageContainer>
        </PlatformSection>

        <Paragraph tw="mt-6">
          <strong>Key Observations from Screenshots:</strong>
        </Paragraph>
        <List>
          <ListItem>
            <strong>Consistent Consent Messaging:</strong> Both Web and Mobile platforms display the
            same clear consent message before any SMS is sent
          </ListItem>
          <ListItem>
            <strong>Explicit User Action Required:</strong> Users must manually enter their phone
            number and click/tap "Send Code" to provide consent - no automatic sending occurs
          </ListItem>
          <ListItem>
            <strong>Prominent Consent Display:</strong> The consent message is displayed in a
            highlighted box, making it impossible to miss before taking action
          </ListItem>
          <ListItem>
            <strong>Platform-Appropriate UI:</strong> While the UI adapts to each platform (web
            modal vs. native mobile modal), the core opt-in workflow and consent messaging remain
            identical
          </ListItem>
          <ListItem>
            <strong>Multiple Entry Points:</strong> The verification flow is accessible from various
            contexts:
            <List>
              <ListItem>
                <strong>Web Platform:</strong> Checkout/booking and profile page
              </ListItem>
              <ListItem>
                <strong>Mobile Application:</strong> Checkout/booking and profile page
              </ListItem>
            </List>
            This ensures users can verify their phone number when needed, with platform-appropriate
            entry points.
          </ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>7. Example Consent Message</SectionTitle>
        <CodeBlock>
          {`By clicking "Send Code", you consent to receive a one-time 
verification code via SMS to [phone number]. 

Message and data rates may apply. This is a transactional message 
for account verification purposes only. We do not send marketing 
or promotional messages via SMS.

Your phone number will only be used for verification and will not 
be shared with third parties for marketing purposes.`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>8. Compliance</SectionTitle>
        <Paragraph>Our SMS verification system complies with:</Paragraph>
        <List>
          <ListItem>TCPA (Telephone Consumer Protection Act) requirements</ListItem>
          <ListItem>Telnyx Verify API compliance standards</ListItem>
          <ListItem>User privacy and consent standards</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>9. Contact</SectionTitle>
        <Paragraph>
          For questions about our SMS verification opt-in process, please contact us at{' '}
          <a
            href="mailto:contact@autopilotee.com"
            tw="text-black"
            style={{ textDecoration: 'underline' }}
          >
            contact@autopilotee.com
          </a>
        </Paragraph>
      </Section>

      <Section>
        <Paragraph tw="text-sm text-text-secondary mt-8">
          Last updated:{' '}
          {new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </Paragraph>
      </Section>
    </PageContainer>
  );
};

export default OptInWorkflowPage;
